import React, { useEffect } from "react";
import { Col, DatePicker, Flex, InputNumber, Row } from "antd";
import { useState } from "react";
import { useRef } from "react";
import Title from "antd/es/typography/Title";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Select } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  baseRouteMakerForSuperAdmin,
  routes,
} from "../../../../../../constants/routeConsts";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import {
  dateObjToDispViewDate,
  sanitizeDataTypeClientSide,
} from "../../../../../../utils/utils";
import dayjs from "dayjs";
import { allowCharactersOnlyForDecimalNumbers } from "../../../../../../utils/appKeyStrokeLimitingUtils";
import {
  createGiftCardAction,
  getGiftCardByIdAction,
  updateGiftCardAction,
} from "../../../../../../redux/services/gift-card/giftCardServices";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();

const DiscountCodesForm = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const start = Form.useWatch("start", { form, preserve: true });
  /* form dependencies */

  /* id dependencies */
  const { selectedGiftCardId } = useSelector((state) => state.giftCards);
  /* id dependencies */

  /* select list dependencies */
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { categoriesSelectList, subCategoriesSelectList } = useSelector(
    (state) => state.categories
  );
  const { systemSettings } = useSelector((state) => state.addonAndSponsorship);
  /* select list dependencies */

  /* function definition for select list API calls */
  const selectListApiCalls = (details) => {};
  /* function definition for select list API calls */

  useEffect(() => {
    /* initial dependencies - Edit */
    if (
      selectedGiftCardId &&
      selectedGiftCardId !== "" &&
      selectedGiftCardId !== null &&
      selectedGiftCardId !== undefined
    ) {
      const dataToBeSent = {
        selectedGiftCardId,
      };
      dispatch(getGiftCardByIdAction(dataToBeSent, setLoading)).then(
        (apiRes) => {
          const giftCards = apiRes;
          const locale = apiRes.locales?.[0];
          selectListApiCalls(giftCards);

          form.setFieldsValue({
            gift_card_name: giftCards?.gift_card_name,
            gift_card_name_ar: locale?.gift_card_name,
            amount: giftCards?.amount,
            start: dayjs(giftCards?.start, "YYYY/MM/DD"),
            expiry: dayjs(giftCards?.expiry, "YYYY/MM/DD"),
            wallet_points: giftCards?.wallet_points,
          });
        }
      );
    } else {
      /* initial dependencies - Edit */
      selectListApiCalls(null);
    }
  }, []);

  /* Form submit handler */
  const onSubmit = (values) => {
    const { start, expiry, ...rest } = values;
    const dataToBeSent = {
      ...rest,
      is_active: true,
      currency: systemSettings?.system_currency,
      start: dateObjToDispViewDate(start, "yyyymmdd"),
      expiry: dateObjToDispViewDate(expiry, "yyyymmdd"),
    };

    if (
      selectedGiftCardId &&
      selectedGiftCardId !== "" &&
      selectedGiftCardId !== null &&
      selectedGiftCardId !== undefined
    ) {
      dataToBeSent._id = selectedGiftCardId;
      dispatch(updateGiftCardAction(dataToBeSent, setLoading));
    } else {
      dispatch(createGiftCardAction(dataToBeSent, setLoading));
    }
    navigate(baseRouteMakerForSuperAdmin(routes.DISCOUNT_CODES));
  };
  /* Form submit handler */

  /* start date and expiry date validation dependencies */
  const disabledStartDate = (currentDate) => {
    return currentDate && dayjs(currentDate).isBefore(dayjs(), "day");
  };

  const disabledEndDate = (value) => {
    if (!start) {
      return true;
    }
    const allowedEndDate = new Date(start);
    return value < allowedEndDate.setDate(allowedEndDate.getDate() + 1);
  };

  /* start date and expiry date validation dependencies */

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex gap="1rem">
          <Button
            type="default"
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() =>
              navigate(baseRouteMakerForSuperAdmin(routes.DISCOUNT_CODES))
            }
          />
          <Title level={2}>
            {selectedGiftCardId &&
            selectedGiftCardId !== "" &&
            selectedGiftCardId !== null &&
            selectedGiftCardId !== undefined
              ? t("editGift")
              : t("addGift")}
          </Title>
        </Flex>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          onFinish={onSubmit}
        >
          <Row gutter={[24, 24]}>
            <Col span={8}>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t(
                      "Modules.DiscountCodes.DiscountCodesForm.giftCardName"
                    )}
                    required={true}
                  />
                }
                name="gift_card_name"
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterGiftCardName"
                    ),
                  },
                  {
                    whitespace: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterGiftCardName"
                    ),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  placeholder={t(
                    "Modules.DiscountCodes.DiscountCodesForm.enterGiftCardName"
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={
                  <RequiredLabel
                    text={
                      `${t(
                        "Modules.DiscountCodes.DiscountCodesForm.giftCardName"
                      )} ${t("CommonUtils.inArabic")}`
                    }
                    required={true}
                  />
                }
                name="gift_card_name_ar"
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterGiftCardName"
                    ),
                  },
                  {
                    whitespace: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterGiftCardName"
                    ),
                  },
                ]}
                normalize={(value) => sanitizeDataTypeClientSide(value)}
              >
                <Input
                  dir="rtl"
                  placeholder={
                    t(
                      "Modules.DiscountCodes.DiscountCodesForm.enterGiftCardName"
                    ) + " in arabic"
                  }
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t("Modules.DiscountCodes.DiscountCodesForm.amount")}
                    required={true}
                  />
                }
                name="amount"
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterAmount"
                    ),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value < 1)
                        return Promise.reject(
                          `${t(
                            "Modules.DiscountCodes.DiscountCodesForm.amount"
                          )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                        );
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder={t(
                    "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterAmount"
                  )}
                  onKeyDown={(e) => allowCharactersOnlyForDecimalNumbers(e)}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t(
                      "Modules.DiscountCodes.DiscountCodesForm.walletPoints"
                    )}
                    required={true}
                  />
                }
                name="wallet_points"
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterWalletPoints"
                    ),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value < 1)
                        return Promise.reject(
                          `${t(
                            "Modules.DiscountCodes.DiscountCodesForm.WalletPoints"
                          )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                        );
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder={t(
                    "Modules.DiscountCodes.DiscountCodesForm.pleaseEnterWalletPoints"
                  )}
                  onKeyDown={(e) => allowCharactersOnlyForDecimalNumbers(e)}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t("Modules.CouponCodes.startDate")}
                    required={true}
                  />
                }
                name="start"
                rules={[
                  {
                    required: true,
                    message: t("Modules.CouponCodes.pleaseSelectStartDate"),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%", cursor: "pointer" }}
                  placeholder={t("Modules.CouponCodes.selectStartDate")}
                  onKeyDown={(event) => event.preventDefault()}
                  disabledDate={disabledStartDate}
                  onChange={() => form.setFieldValue("expiry", null)}
                  format={"DD-MM-YYYY"}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <RequiredLabel
                    text={t(
                      "Modules.DiscountCodes.DiscountCodesForm.expiryDate"
                    )}
                    required={true}
                  />
                }
                name="expiry"
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.DiscountCodes.DiscountCodesForm.pleaseSelectExpiryDate"
                    ),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%", cursor: "pointer" }}
                  placeholder={t(
                    "Modules.DiscountCodes.DiscountCodesForm.selectExpiryDate"
                  )}
                  onKeyDown={(event) => event.preventDefault()}
                  disabledDate={disabledEndDate}
                  format={"DD-MM-YYYY"}
                />
              </Form.Item>
            </Col>
            <Col span={24} style={{ textAlign: "center" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
              >
                {t("CommonUtils.submit")}
              </Button>
            </Col>
          </Row>
        </Form>
      </Flex>
    </div>
  );
};

export default DiscountCodesForm;
